import React, { useEffect, useRef, useState } from 'react';
import Header from "./common/Header";
import './Saved.css';
import { Link } from "react-router-dom";
import { formattedPrice } from '../utils/priceFormatter';
import httpService from '../services/HttpService';
import SizeChartModal from './common/SizeChartModal';
import useIsMobile from '../hooks/useIsMobile';

const Saved = () => {
    const [favourites, setFavourites] = useState([]);
    const [activeProduct, setActiveProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedSize, setSelectedSize] = useState(null);
    const [notification, setNotification] = useState(null);
    const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);
    const [sizeChart, setSizeChart] = useState(null);
    const effectRan = useRef(false);
    const [alertedStocks, setAlertedStocks] = useState(new Set());
    const [desktopModalOpen, setDesktopModalOpen] = useState(false);
    const isMobile = useIsMobile();
    const headerRef = useRef();

    const setAlertedItems = () => {
        const initialAlerted = new Set(
            activeProduct?.stocks.filter((s) => s.alert_count > 0).map((s) => s.id)
        );

        setAlertedStocks(initialAlerted);
    }

    useEffect(() => {
        setAlertedItems();
    }, [activeProduct]);

    const fetchSizeChart = async () => {
        try {
            const response = await httpService.get(`/size-chart?category_id=${activeProduct.category_id}&brand_id=${activeProduct.brand_id}&product_id=${activeProduct.id}`);
            const res = await response.json();

            setSizeChart(res.table);
            setIsSizeChartOpen(true);
        } catch (error) {
            console.error("Failed to fetch size chart:", error);
        }
    };

    const handleSizeSelect = (size) => {
        setSelectedSize(size);
        handleAddProductToCart(activeProduct.id, size);
    };

    const closeModal = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsModalOpen(false);
            setIsClosing(false);
        }, 600);
    };

    const closeDesktopModal = () => setDesktopModalOpen(false);

    const handleAddProductToCart = async (productId, size) => {
        const cartData = {
            product_id: productId,
            size,
            count: 1,
        };

        try {
            const response = await httpService
                .post('/cart', cartData);

            setIsModalOpen(false);
            setDesktopModalOpen(false);

            if (response.success || response.status === 200 || response.status === 201) {
                window.ym && window.ym(101728239, 'reachGoal', 'add_to_cart');
                setNotification('Товар успешно добавлен в корзину');
                headerRef.current?.incrementCart();
                setSelectedSize(null);
            } else {
                throw new Error('Error adding to cart: ' + (response.message || 'Server error'));
            }
        } catch (error) {
            console.error('Error:', error);
            setNotification(`Ошибка при добавлении товара в корзину: ${error.message || 'Неизвестная ошибка'}`);
        }

        setTimeout(() => setNotification(null), 3000);
    };

    useEffect(() => {
        if (effectRan.current) return;
        effectRan.current = true;
        const fetchFavourites = async () => {
            try {
                const response = await httpService.get("/favourites");
                const data = await response.json();
                setFavourites(data);
            } catch (error) {
                console.error("Error fetching favourites:", error);
            }
        };

        fetchFavourites();
    }, []);

    const toggleRestock = async (stockId, size) => {
        try {
            const isAlerted = alertedStocks.has(stockId);
            const endpoint = "/restock-alerts";
            const method = isAlerted ? "delete" : "post";
            const payload = { product_id: activeProduct.id, size };

            const response = await httpService[method](endpoint, payload);
            const res = await response.json();
    
            if ((isAlerted && res?.message) || (!isAlerted && res?.id)) {
                setAlertedStocks(prev => {
                    const updated = new Set(prev);
                    isAlerted ? updated.delete(stockId) : updated.add(stockId);
                    return updated;
                });
    
                setNotification(isAlerted ? "Оповещение удалено" : "Оповещение о пополнении отправлено");
            }
        } catch (error) {
            console.error("Error:", error);
            setNotification("Ошибка");
        }
    
        setTimeout(() => setNotification(null), 1500);
    };

    const handleRemoveItem = async (favouriteId) => {
        if (!window.confirm('Удалить из избранного?')) {
            return;
        }

        try {
            await httpService.delete(`/favourites/${favouriteId}`);
            setFavourites((prev) => prev.filter((fav) => fav.id !== favouriteId));
        } catch (error) {
            console.error("Error removing favorite:", error);
        }
    };
    
    return (
        <>
            <Header ref={headerRef} />
            <main className="cart-main">
                <div className="cart-container">
                    <div className="cart-title">Сохраненные</div>

                    {favourites.length === 0 ? (
                        <p>Нет сохраненных</p>
                    ) : (
                        <div className="cart-items-list">
                            {favourites.map((fav) => {
                                const item = fav.product;

                                return (
                                    <div key={fav.id} className="cart-item saved">
                                        <div className="cart-item-left">
                                            <Link className="link" to={`/product/${item.id}`}>
                                                <img
                                                    src={item.main_image.link}
                                                    alt={item.name}
                                                    className="cart-item-image saved"
                                                />
                                            </Link>
                                        </div>
                                        <div className="cart-item-middle saved">
                                            <div>
                                                <p className="cart-item-brand-name">{item.brand.name}</p>
                                                <p className="cart-item-name">{item.name}</p>
                                                <p className="cart-item-size">Size: {item.size}</p>
                                            </div>
                                            <div className="saved-item-price">
                                                {formattedPrice(item.price, item.actual_price, item.sale)}
                                                <button
                                                    className="add-cart-button"
                                                    onClick={() => {
                                                        setActiveProduct(item);
                                                        setAlertedItems();
                                                        isMobile ? setIsModalOpen(true) : setDesktopModalOpen(true);
                                                    }}
                                                >
                                                    В корзину
                                                </button>
                                            </div>
                                        </div>
                                        <div className="cart-item-right">
                                            <span
                                                onClick={() => handleRemoveItem(fav.id)}
                                                className="material-symbols-sharp remove-button"
                                            >
                                                close
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className={isModalOpen ? "chart-modal-overlay" : ""} onClick={() => {setIsModalOpen(false)}}></div>

                {isModalOpen && activeProduct && (
                    <div className={`size-selector-modal ${isClosing ? "fade-out" : "fade-in"}`}
                    onClick={closeModal}>
                        <div className="size-selector-container" onClick={(e) => e.stopPropagation()}>
                        <div className="chart-section">
                            <span className="chart-section-size-text">Выберите размер</span>
                            <button className="product-chart-btn" onClick={fetchSizeChart}>
                                <span className="material-symbols-sharp">straighten</span> Таблица размеров
                            </button>
                        </div>
                            <div className="size-list">
                                {activeProduct.stocks.map((stock, index) => (
                                    <button 
                                        key={index}
                                        style={{background: stock.count === 0 ? '#ececec' : 'white', border: stock.count === 0 ? 'none': ''}}
                                        className={`size-button ${selectedSize === stock.size ? "active" : ""}`} 
                                        onClick={() => {
                                            if(stock.count === 0) {
                                                toggleRestock(stock.id, stock.size)
                                            } else {
                                                setSelectedSize(stock.size);
                                                handleAddProductToCart(activeProduct.id, stock.size);
                                                closeModal();
                                            }
                                        }}
                                    >
                                        {stock.size}
                                        
                                        {stock.count === 1 && (
                                            <span className="size-quantity">{stock.count} шт.</span>
                                        )}

                                        {stock.count === 0 && (
                                            <span
                                                className={`material-symbols-sharp alert-icon ${alertedStocks.has(stock.id) ? "filled" : "outlined"}`}
                                            >
                                                {alertedStocks.has(stock.id) ? "notifications_none" : "notifications"}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                                {desktopModalOpen && (
                    <div className="desktop-modal-overlay" onClick={closeDesktopModal}>
                        <div className="desktop-size-modal" onClick={e => e.stopPropagation()}>
                        <button
                            className="hamburger close"
                            onClick={closeDesktopModal}
                            aria-label="Toggle Modal"
                        >
                            <span className="material-symbols-sharp">
                                close
                            </span>
                        </button>
                            <h2>Выберите размер</h2>

                            <button className="desktop-size-chart-btn" onClick={fetchSizeChart}>
                                Таблица размеров
                            </button>

                            <div className="size-list">
                                {activeProduct?.stocks.map((stock, index) => (
                                    <button
                                        key={index}
                                        style={{
                                            background: stock.count === 0 ? '#ececec' : 'white',
                                            border: stock.count === 0 ? 'none' : '',
                                        }}
                                        className={`size-button ${selectedSize === stock.size ? 'active' : ''}`}
                                        onClick={() => {
                                            if (stock.count === 0) {
                                                toggleRestock(stock.id, stock.size);
                                            } else {
                                                setSelectedSize(stock.size);
                                                handleAddProductToCart(activeProduct.id, stock.size);
                                                closeModal();
                                            }
                                        }}
                                    >
                                        {stock.size}
                                        {stock.count === 1 && (
                                            <span className="size-quantity">{stock.count} шт.</span>
                                        )}
                                        {stock.count === 0 && (
                                            <span className={`material-symbols-sharp alert-icon ${alertedStocks.has(stock.id) ? 'filled' : 'outlined'}`}>
                                                {alertedStocks.has(stock.id) ? 'notifications_none' : 'notifications'}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {isSizeChartOpen && <SizeChartModal onSizeSelect={handleSizeSelect} tableData={sizeChart} closeChartModal={() => setIsSizeChartOpen(false)} />}
                {notification && (
                    <div className="notification">
                        {notification}
                    </div>
                )}
            </main>
        </>
    );
};

export default Saved;